<template>
  <div :class="`iconn_button ${fullWidth ? 'full_width' : ''}`">
    <VBtn
      :id="id"
      style="letter-spacing: 0"
      class="button"
      :class="buttonClass"
      :disabled="disabled"
      :ripple="false"
      :loading="loading"
      :slim="slim"
      :min-width="0"
      @click="onClick"
    >
      <Tooltip
        v-if="isConnectMeetBtn"
        text="You need to be connected or registered to the same event to book a meeting"
        :max-width="300"
      />
      <!-- Left icon -->
      <IconMaterial
        v-if="leftIcon"
        class="left-icon"
        :class="{ 'margin-right_xs-df': text }"
        :style="{ marginBottom: bottomMargin }"
        :icon="leftIcon"
        :size="iconSize"
        :variant="iconVariant"
        :color="
          customColorIcon
            ? customColorIcon
            : type === 'primary' && !disabled
              ? 'white'
              : disabled
                ? ''
                : type === 'secondary_flat'
                  ? '#64748B'
                  : type
        "
      />
      <!-- Button text -->
      <slot>
        <p>{{ text }}</p>
      </slot>

      <!-- Right icon -->
      <IconMaterial
        v-if="rightIcon"
        class="right-icon"
        :class="{ 'margin-left_xs-df': text }"
        :style="{ marginBottom: bottomMargin }"
        :icon="rightIcon"
        :size="iconSize"
        :color="
          customColorIcon
            ? customColorIcon
            : type === 'primary' && !disabled
              ? 'white'
              : disabled
                ? ''
                : type === 'secondary_flat'
                  ? '#64748B'
                  : type
        "
      />
    </VBtn>
  </div>
</template>

<script setup lang="ts">
import { defineProps, defineEmits, computed, ref } from 'vue';
import IconMaterial from './IconMaterial.vue';
import Tooltip from './Tooltip.vue';

const props = defineProps({
  id: {
    type: String,
    default: '',
  },
  text: {
    type: String,
    default: '',
  },
  leftIcon: {
    type: String,
    default: '',
  },
  rightIcon: {
    type: String,
    default: '',
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'md',
  },
  sizeIcon: {
    type: Number,
    default: null,
  },
  type: {
    type: String,
    default: 'primary',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  colorIcon: {
    type: String,
    default: '',
  },
  iconVariant: {
    type: String,
    default: '',
  },
  slim: {
    type: Boolean,
    default: false,
  },
  fontWeight: {
    type: String as () => 'thin' | 'light' | 'regular' | 'medium' | 'bold' | 'black',
    default: 'regular',
  },
  isConnectMeetBtn: {
    type: Boolean,
    default: false,
  },
  square: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['onClick']);

const onClick = () => {
  emit('onClick');
};

const iconNeedsBottomMargin = ref(['icon_right', 'icon_iconnections', 'icon_save']);

const iconSize = computed(() => {
  if (props.sizeIcon) {
    return props.sizeIcon;
  }
  switch (props.size) {
    case 'sm':
      return 16;
    case 'md':
      return 16;
    case 'lg':
      return 20;
    default:
      return 16;
  }
});

const customColorIcon = computed(() => {
  if (props.colorIcon) {
    return props.colorIcon;
  }
  return '';
});

const bottomMargin = computed(() => {
  if (iconNeedsBottomMargin.value.includes(props.rightIcon)) {
    return '12px';
  }
  if (iconNeedsBottomMargin.value.includes(props.leftIcon)) {
    return '2.5px';
  }
  return '0';
});

const buttonClass = computed(() => {
  const fullWidthClass = props.fullWidth ? 'full_width' : '';
  const slimClass = props.slim ? 'slim' : '';
  const fontWeightClass = `font-weight-${props.fontWeight}`;
  const sizeClass = `button_${props.size}`;
  const typeClass = `button_${props.type}`;
  const square = props.square ? 'square' : '';
  return [fullWidthClass, slimClass, fontWeightClass, sizeClass, typeClass, square];
});
</script>

<style lang="scss" scoped>
@import '@foundation';

.full_width {
  width: 100%;
}

.button {
  padding-left: 24px !important;
  padding-right: 24px !important;

  &.square {
    padding-left: 20px !important;
    padding-right: 20px !important;
  }

  &.slim {
    padding-left: 12px !important;
    padding-right: 12px !important;
  }

  // Size classes
  &_sm {
    @extend .height_lg-important;
    @extend .medium-body-sm;
  }

  &_md {
    @extend .height_xl-important;
    @extend .medium-body-sm;
  }

  &_lg {
    @extend .height_2xl-important;
    width: 210px;
    @extend .medium-body-md;
  }

  // Type classes
  &_primary {
    @extend .typography_white;
    @extend .gray-shadow-xs;
    background-color: map-get($primary, 'purple-500') !important;

    &:hover {
      background-color: map-get($primary, 'purple-300') !important;
    }

    &:active {
      background-color: map-get($primary, 'purple-700') !important;
    }

    &:disabled {
      @extend .typography_disabled;
      background-color: map-get($grey_scale, 'grey-200') !important;
    }
  }
  &_primary_flat {
    @extend .typography_primary;
    border: 1px solid map-get($neutral, 'neutral-300');
    background-color: rgb(var(--v-theme-surface)) !important;
    box-shadow: none;

    &:hover {
      border-color: map-get($primary, 'purple-500') !important;
    }

    &:active {
      @extend .border_stroke_md;
      border-color: map-get($primary, 'purple-500') !important;
    }

    &:disabled {
      @extend .typography_disabled;
      background-color: map-get($grey_scale, 'grey-200') !important;
    }
  }

  &_secondary {
    @extend .typography_default;
    @extend .gray-shadow-xs;
    background-color: rgb(var(--v-theme-surface));
    border: 1px solid map-get($primary, 'purple-500');

    &:hover {
      @extend .border_stroke_md;
      border-color: map-get($primary, 'purple-500') !important;
    }

    &:active {
      @extend .border_stroke_md;
      border-color: map-get($neutral, 'neutral-900') !important;
    }

    &:disabled {
      @extend .typography_disabled;
      background-color: map-get($basic, 'white') !important;
      border-color: map-get($neutral, 'neutral-700') !important;
    }
  }

  &_secondary_flat {
    @extend .typography_default;
    background-color: rgb(var(--v-theme-surface)) !important;
    border: 1px solid map-get($primary, 'purple-500') !important;
    box-shadow: none;

    &:hover {
      border: 2px solid map-get($primary, 'purple-600') !important;
    }

    &:active {
      border: 2px solid map-get($primary, 'purple-600') !important;
    }

    &:disabled {
      @extend .typography_disabled;
      background-color: map-get($basic, 'white') !important;
      border-color: map-get($neutral, 'neutral-700') !important;
    }
  }

  &_success-outline {
    @extend .typography_success;
    @extend .border_stroke_sm_df;
    @extend .gray-shadow-xs;
    background-color: rgb(var(--v-theme-surface)) !important;
    border-color: map-get($success, 'green-800') !important;

    &:hover {
      @extend .border_stroke_md;
    }

    &:active {
      @extend .border_stroke_md;
      border-color: map-get($success, 'green-900') !important;
    }

    &:disabled {
      @extend .typography_disabled;
      background-color: map-get($basic, 'white') !important;
      border-color: map-get($neutral, 'neutral-700') !important;
    }
  }

  &_success-filled {
    @extend .typography_white;
    @extend .gray-shadow-xs;
    background-color: map-get($success, 'green-700') !important;

    &:hover {
      background-color: map-get($success, 'green-600') !important;
    }

    &:active {
      background-color: map-get($success, 'green-900') !important;
    }

    &:disabled {
      @extend .typography_disabled;
      background-color: map-get($grey_scale, 'grey-200') !important;
    }
  }

  &_text {
    @extend .typography_info;
    border: none;
    background-color: transparent !important;
    box-shadow: none;

    &:hover {
      font-weight: 700;
      color: map-get($secondary, 'blue-700');
    }

    &:active {
      font-weight: 700;
      color: map-get($secondary, 'blue-900');
    }

    &:disabled {
      @extend .typography_disabled;
      background-color: map-get($grey_scale, 'grey-200') !important;
    }
  }

  &_warning-outline {
    @extend .typography_warning;
    @extend .border_stroke_sm_df;
    @extend .gray-shadow-xs;
    background-color: rgb(var(--v-theme-surface)) !important;
    border-color: map-get($warning, 'orange-500') !important;

    &:hover {
      @extend .border_stroke_md;
    }

    &:active {
      @extend .border_stroke_md;
      border-color: map-get($warning, 'orange-700') !important;
    }

    &:disabled {
      @extend .typography_disabled;
      background-color: map-get($basic, 'white') !important;
      border-color: map-get($neutral, 'neutral-700') !important;
    }
  }

  &_info-outline {
    @extend .typography_info;
    @extend .border_stroke_sm_df;
    @extend .gray-shadow-xs;
    background-color: rgb(var(--v-theme-surface)) !important;
    border-color: map-get($secondary, 'blue-600') !important;
    box-shadow: none;

    &:hover {
      @extend .border_stroke_md;
    }

    &:active {
      @extend .border_stroke_md;
      border-color: map-get($secondary, 'blue-800') !important;
    }

    &:disabled {
      @extend .typography_disabled;
      background-color: map-get($basic, 'white') !important;
      border-color: map-get($neutral, 'neutral-700') !important;
    }
  }

  &_error-outline {
    @extend .typography_error;
    @extend .border_stroke_sm_df;
    @extend .gray-shadow-xs;
    background-color: rgb(var(--v-theme-surface)) !important;
    border-color: map-get($error, 'red-500') !important;

    &:hover {
      @extend .border_stroke_md;
    }

    &:active {
      @extend .border_stroke_md;
      border-color: map-get($error, 'red-700') !important;
    }

    &:disabled {
      @extend .typography_disabled;
      background-color: map-get($basic, 'white') !important;
      border-color: map-get($neutral, 'neutral-700') !important;
    }
  }
}
</style>

<style lang="scss">
.iconn_button {
  .v-btn {
    text-transform: none;
  }

  .v-btn__overlay {
    background-color: unset;
  }
}

.left-icon {
  display: flex;
  justify-content: flex-end;
}

.right-icon {
  display: flex;
  justify-content: flex-end;
}
</style>
